"use client";

import { createContext, ReactNode, useContext, useEffect, useState } from "react";

/**
 * The focus modality can be either "keyboard" or "pointer".
 * - "keyboard": The user is navigating using the keyboard (Tab key, arrow keys, etc.)
 * - "pointer": The user is navigating using a pointer device (mouse, touch, etc.)
 */
type FocusModality = "keyboard" | "pointer";

const FocusVisibleContext = createContext<FocusModality>("pointer");

/**
 * Returns the current focus modality.
 *
 * Use this to conditionally show focus rings only during keyboard navigation.
 */
export function useFocusVisible(): { isFocusVisible: boolean; modality: FocusModality } {
  const modality = useContext(FocusVisibleContext);
  return {
    isFocusVisible: modality === "keyboard",
    modality,
  };
}

export interface FocusVisibleProviderProps {
  children: ReactNode;
}

/**
 * A provider that tracks focus modality (keyboard vs pointer) and sets a
 * `data-focus-visible-mode` attribute on the document element.
 *
 * This allows CSS to conditionally show focus rings only during keyboard navigation,
 * solving the issue where focus rings appear on elements programmatically focused
 * when popovers/modals open via click.
 *
 * Place this provider as high as possible in your component tree, ideally in the root layout.
 *
 * CSS can then use the following selector to only show focus rings during keyboard navigation:
 * ```css
 * html[data-focus-visible-mode="keyboard"] .my-element:focus-visible {
 *   outline: 2px solid blue;
 * }
 * ```
 */
export function FocusVisibleProvider({ children }: FocusVisibleProviderProps) {
  const [modality, setModality] = useState<FocusModality>("pointer");

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only switch to keyboard mode for navigation keys
      // Tab is the primary indicator of keyboard navigation
      // Arrow keys are also used for navigating within components
      if (
        e.key === "Tab" ||
        e.key === "ArrowUp" ||
        e.key === "ArrowDown" ||
        e.key === "ArrowLeft" ||
        e.key === "ArrowRight"
      ) {
        setModality("keyboard");
        document.documentElement.setAttribute("data-focus-visible-mode", "keyboard");
      }
    };

    const handlePointerDown = () => {
      setModality("pointer");
      document.documentElement.setAttribute("data-focus-visible-mode", "pointer");
    };

    // Set initial state
    document.documentElement.setAttribute("data-focus-visible-mode", modality);

    // Listen for keyboard and pointer events
    document.addEventListener("keydown", handleKeyDown, true);
    document.addEventListener("mousedown", handlePointerDown, true);
    document.addEventListener("pointerdown", handlePointerDown, true);

    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      document.removeEventListener("mousedown", handlePointerDown, true);
      document.removeEventListener("pointerdown", handlePointerDown, true);
    };
  }, [modality]);

  return <FocusVisibleContext value={modality}>{children}</FocusVisibleContext>;
}
